<template>
    <FciDialog :title="this.$f.tc('buttonEditor')" v-model="show">
        <div class="ButtonEditor">
            <v-form v-model="formValid" ref="form" class="pa-3">
                <v-radio-group v-if="!isRatingCard" row dense class="mt-0" v-model="type" :label="this.$f.tc('function')">
                    <template v-for="type in typeList">
                        <v-radio
                            v-if="allowButtonType[type.value]"
                            :key="type.value"
                            :label="type.text"
                            :value="type.value"
                        ></v-radio>
                    </template>
                </v-radio-group>
                <v-layout align-center>
                    <v-text-field
                        outlined
                        dense
                        :label="this.$f.tc('name')"
                        v-model="title"
                        :rules="[formRules.ruleRequired, formRules.ruleNoHtmlTag]"
                        maxlength="100"
                    ></v-text-field>
                    <div class="ml-3" style="margin-bottom: 24px" v-if="includeThumb">
                        <div class="thumb-icon" @click="selectThumb">
                            <img :src="thumb_url" alt="">
                        </div>
                    </div>
                    <v-tooltip top activator="#warningButtonNameFacebook">
                        <span style="color: #fff">{{ $f.tc('warningButtonNameFacebook') }}</span>
                    </v-tooltip>
                    <v-icon
                        color="yellow"
                        style="margin-bottom: 27px"
                        class="ml-2"
                        id="warningButtonNameFacebook"
                    >mdi-alert-circle-outline</v-icon>
                </v-layout>
                <template v-if="type === ButtonType.GO_TO_STEP">
                    <v-layout>
                        <v-autocomplete
                            outlined
                            dense
                            :label="this.$f.tc('step')"
                            v-model="payload"
                            :items="stepList"
                            item-text="name"
                            item-value="name"
                            :rules="formRules.required"
                        ></v-autocomplete>
                        <v-btn class="ml-3" outlined @click="createStep">
                            <v-icon color="primary" class="mr-1" small>mdi-plus-circle</v-icon>
                            {{ $f.tc('step') }}
                        </v-btn>
                    </v-layout>
                    <v-layout v-if="isRatingCard">
                        <v-autocomplete
                            outlined
                            dense
                            :label="this.$f.tc('rateLevel')"
                            v-model="rate_level"
                            :items="rateLevelList"
                            item-text="name"
                            item-value="name"
                            :rules="formRules.required"
                        ></v-autocomplete>
                    </v-layout>
                    <div>{{ $f.tc('dataPayload') }}</div>
                    <table>
                        <tr v-for="(attribute, i) in attributes" :key="i">
                            <td>
                                <v-autocomplete
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('variable')"
                                    v-model="attribute.variable"
                                    :items="getVariableItemList(i)"
                                    :rules="formRules.required"
                                    @change="updateVariableMap"
                                ></v-autocomplete>
                            </td>
                            <td>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('value')"
                                    v-model="attribute.value"
                                ></v-text-field>
                            </td>
                            <td>
                                <v-btn icon @click="deleteAttribute(i)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </table>
                    <v-layout class="mt-3">
                        <v-btn outlined @click="addAttribute" :disabled="isDisableAdd">
                            <v-icon color="primary" small class="mr-1">mdi-plus-circle</v-icon>
                            {{ $f.tc('dataPayload') }}
                        </v-btn>
                        <v-flex></v-flex>
                        <v-btn outlined @click="addScenarioVariable">
                            <v-icon color="primary" small class="mr-1">mdi-plus-circle</v-icon>
                            {{ $f.tc('variable') }}
                        </v-btn>
                    </v-layout>
                </template>
                <v-text-field
                    outlined
                    dense
                    label="URL"
                    v-if="type === ButtonType.URL"
                    v-model="url"
                    :rules="[formRules.ruleRequired, formRules.ruleURL]"
                ></v-text-field>
                <template v-if="type === ButtonType.CALL_PHONE">
                    <v-text-field
                        outlined
                        dense
                        :label="this.$f.tc('phone')"
                        v-model="phone_call"
                        :rules="[formRules.ruleRequired, formRules.rulePhone]"
                    ></v-text-field>
                    <div class="centerY">
                        <v-icon color="yellow" class="mr-1">mdi-alert-circle-outline</v-icon>
                        {{ $f.tc('platformRequirePhoneCountryCode') }}
                    </div>
                </template>
                <template v-if="type === ButtonType.WEBVIEW">
                    <v-select
                        outlined
                        dense
                        label="Web View"
                        v-model="webview"
                        :rules="formRules.required"
                        :items="webViewList"
                        item-text="name"
                        item-value="code"
                    ></v-select>
                    <v-text-field
                        outlined
                        dense
                        :label="self.$f.tc('parameter')"
                        v-model="params"
                    ></v-text-field>
                    <div class="centerY">
                        <v-icon color="yellow" class="mr-1">mdi-alert-circle-outline</v-icon>
                        {{ $f.tc('webviewOnlyWork') }}
                    </div>
                </template>
                <template v-if="type === ButtonType.KNOWLEDGE">
                    <v-autocomplete
                            outlined
                            dense
                            :label="this.$f.tc('intent')"
                            v-model="intent"
                            :items="intentList"
                            item-text="label"
                            item-value="label"
                            :rules="formRules.required"
                        ></v-autocomplete>
                    <div>{{ $f.tc('variables') }}</div>
                    <table>
                        <tr v-for="(attribute, i) in variables" :key="i">
                            <td>
                                <v-autocomplete
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('variable')"
                                    v-model="attribute.variable"
                                    :items="getVariableItemList(i)"
                                    :rules="formRules.required"
                                    @change="updateVariableListMap"
                                ></v-autocomplete>
                            </td>
                            <td>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('value')"
                                    v-model="attribute.value"
                                ></v-text-field>
                            </td>
                            <td>
                                <v-btn icon @click="deleteVariable(i)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </table>
                    <v-layout class="mt-3">
                        <v-btn outlined @click="addVariable" :disabled="isDisableAdd">
                            <v-icon color="primary" small class="mr-1">mdi-plus-circle</v-icon>
                            {{ $f.tc('variable') }}
                        </v-btn>
                        <v-flex></v-flex>
                    </v-layout>
                    <div class="mt-2">{{ $f.tc('entityType') }}</div>
                    <table>
                        <tr v-for="(attribute, i) in entities" :key="i">
                            <td>
                                <v-autocomplete
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('entityType')"
                                    v-model="attribute.entity"
                                    :items="getEntityItemList(i)"
                                    :rules="formRules.required"
                                    @change="updateEntityListMap"
                                ></v-autocomplete>
                            </td>
                            <td>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :label="self.$f.tc('value')"
                                    v-model="attribute.real_value"
                                ></v-text-field>
                            </td>
                            <td>
                                <v-btn icon @click="deleteEntity(i)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </table>
                    <v-layout class="mt-3">
                        <v-btn outlined @click="addEntity" :disabled="isDisableAdd">
                            <v-icon color="primary" small class="mr-1">mdi-plus-circle</v-icon>
                            {{ $f.tc('entityType') }}
                        </v-btn>
                        <v-flex></v-flex>
                    </v-layout>
                </template>
            </v-form>
            <div style="border-top: 1px solid #D0D4E2"></div>
            <v-layout class="pa-3">
                <v-btn
                    color="#bd2130"
                    dark
                    @click="deleteButton"
                    v-if="item !== null"
                >{{ $f.tc('delete') }}</v-btn>
                <v-flex></v-flex>
                <v-btn class="mr-3" @click="cloneExistedButton" outlined color="primary">
                    {{ $f.tc('cloneExistedButton') }}
                </v-btn>
                <v-btn
                    class="mr-3"
                    outlined
                    color="primary"
                    @click="copyPayload"
                    v-if="type === ButtonType.GO_TO_STEP"
                >{{ $f.tc('copyPayload') }}</v-btn>
                <v-btn color="primary" @click="save">{{ $f.tc('save') }}</v-btn>
            </v-layout>
            <input type="text" ref="fuckVuetify" class="hidden-input">
        </div>
    </FciDialog>
</template>

<script>
import { mapState } from 'vuex'
import FciDialog from '@/components/FciDialog'
import DialogPromiseMixin from '@/modules/fptai/mixins/DialogPromiseMixin'
import FormMixin from '@/modules/fptai/mixins/FormMixin'
import { AppEventBusType } from '@/eventbus/app.eventbus'
import { CardService } from '@/services/bot.service'
import { ButtonType, CardType, DefaultAllowButtonType, QuickReplyAllowButtonType, RatingAllowButtonType } from '@/utils/const'
import { v4 as uuid } from 'uuid'

export default {
    name: 'ButtonEditor',
    components: { FciDialog },
    mixins: [DialogPromiseMixin, FormMixin],
    data () {
        return {
            self: this,
            ButtonType,
            isRatingCard: false,
            rateLevelList: ['1', '2', '3', '4', '5'],
            rate_level: null,
            // data
            card: null,
            item: null,
            selectedVariableMap: {},
            selectedVariableListMap: {},
            selectedEntityListMap: {},
            allowButtonType: DefaultAllowButtonType,
            // model data
            type: '',
            title: '',
            includeThumb: false,
            thumb_url: '',
            // go to step
            payload: '',
            intent: '',
            attributes: [],
            variables: [],
            entities: [],
            // url
            url: '',
            // phone
            phone_call: '',
            // webview
            webview: '',
            params: ''
        }
    },
    computed: {
        ...mapState({
            stepList: state => state.scenario.stepList,
            intentList: state => state.scenario.intentList,
            variableList: state => state.scenario.variableList,
            entityList: state => state.scenario.entityList,
            webViewList: state => state.scenario.webViewList,
            appConfigList: state => state.scenario.appConfigList
        }),
        typeList () {
            return [{
                text: this.$f.tc('goToStep'),
                value: ButtonType.GO_TO_STEP
            }, {
                text: 'URL',
                value: ButtonType.URL
            }, {
                text: this.$f.tc('callPhone'),
                value: ButtonType.CALL_PHONE
            }, {
                text: 'Web view',
                value: ButtonType.WEBVIEW
            }]
        },
        isDisableAdd () {
            if (this.attributes.length < 1) {
                return false
            }
            let lastItem = this.attributes[this.attributes.length - 1]
            return !lastItem.variable
        }
    },
    methods: {
        open ({ card, item }) {
            const isBotKnowledgeBaseEnabled = this.appConfigList.some(config =>
                config.config_key === 'bot_knowledge_base_enable' && config.config_value === '1'
            )
            if (isBotKnowledgeBaseEnabled && !this.typeList.some(item => item.text === 'Knowledge')) {
                this.typeList.push({
                    text: 'Knowledge',
                    value: ButtonType.KNOWLEDGE
                })
            } else {
                if (!isBotKnowledgeBaseEnabled && this.typeList.some(item => item.text === 'Knowledge')) {
                    this.typeList.pop()
                }
            }
            this.card = card
            this.item = item
            this.includeThumb = false
            this.allowButtonType = DefaultAllowButtonType
            this.isRatingCard = false
            if (card.card_type_id === CardType.ZALO_LIST_TEMPLATE) {
                this.includeThumb = true
            } else if (card.card_type_id === CardType.QUICK_REPLY) {
                this.allowButtonType = QuickReplyAllowButtonType
            } else if (card.card_type_id === CardType.RATING) {
                this.allowButtonType = RatingAllowButtonType
                this.isRatingCard = true
            }
            this.updateData(item)
            this.show = true
            return new Promise((resolve, reject) => {
                this.promise = {
                    resolve,
                    reject
                }
            })
        },
        updateData (item) {
            this.type = this.getType(item)
            if (item) {
                this.title = item.title
                this.thumb_url = item.image_url || ''
                for (let i in ButtonType) {
                    let type = ButtonType[i]
                    this[type] = type === this.type ? item[type] : ''
                }
                if (this.type === ButtonType.GO_TO_STEP) {
                    let split = item.payload ? item.payload.split('#') : []
                    this.payload = split[0] ? split[0] : null
                    this.attributes = []
                    if (split[1]) {
                        let attributes = JSON.parse(this.$ut.b64DecodeUnicode(split[1])).set_attributes
                        for (let i in attributes) {
                            if (i === 'rate_level' && this.isRatingCard) {
                                this.rate_level = attributes[i]
                            } else {
                                this.attributes.push({
                                    variable: i,
                                    value: attributes[i]
                                })
                            }
                        }
                    }
                } else if (this.type === ButtonType.WEBVIEW) {
                    this.params = item.webview_params
                } else if (this.type === ButtonType.KNOWLEDGE) {
                    let split = item.request ? item.request.split('#') : []
                    this.intent = split[0] ? split[0] : ''
                    this.variables = []
                    this.entities = []
                    if (split[1]) {
                        this.variables = JSON.parse(this.$ut.b64DecodeUnicode(split[1])).variables
                        this.entities = JSON.parse(this.$ut.b64DecodeUnicode(split[1])).entities
                    }
                }
            } else {
                this.title = ''
                this.thumb_url = 'https://bot.fpt.ai/static-v35/fptai/img/default-image.png'
                for (let i in ButtonType) {
                    this[ButtonType[i]] = ''
                }
                this.intent = ''
                this.attributes = []
                this.variables = []
                this.entities = []
                this.params = ''
                if (this.$refs.form) {
                    this.$refs.form.resetValidation()
                }
                this.type = ButtonType.GO_TO_STEP
            }
            this.updateVariableMap()
        },
        getType (button) {
            if (!button) {
                return ButtonType.GO_TO_STEP
            }
            for (let i in ButtonType) {
                if (button[ButtonType[i]]) {
                    return ButtonType[i]
                }
            }
            return ButtonType.GO_TO_STEP
        },
        addAttribute () {
            this.attributes.push({
                variable: '',
                value: ''
            })
        },
        deleteAttribute (index) {
            this.attributes.splice(index, 1)
        },
        addVariable () {
            this.variables.push({
                variable: '',
                value: ''
            })
        },
        deleteVariable (index) {
            this.variables.splice(index, 1)
        },

        addEntity () {
            this.entities.push({
                variable: '',
                real_value: ''
            })
        },
        deleteEntity (index) {
            this.entities.splice(index, 1)
        },
        save () {
            if (!this.formValid) {
                this.$refs.form.validate()
                return
            }
            if (this.type === ButtonType.GO_TO_STEP) {
                let check = {}
                for (let attribute of this.attributes) {
                    if (check[attribute.variable]) {
                        this.$appService.inform(
                            this.$f.tc('duplicateVariable') + ': ' + attribute.variable
                        )
                        return
                    } else {
                        check[attribute.variable] = true
                    }
                }
            }
            if (this.type === ButtonType.KNOWLEDGE) {
                let check = {}
                for (let variable of this.variables) {
                    if (check[variable.variable]) {
                        this.$appService.inform(
                            this.$f.tc('duplicateVariable') + ': ' + variable.variable
                        )
                        return
                    } else {
                        check[variable.variable] = true
                    }
                }
                check = {}
                for (let entity of this.entities) {
                    if (check[entity.entity]) {
                        this.$appService.inform(
                            this.$f.tc('duplicateVariable') + ': ' + entity.entity
                        )
                        return
                    } else {
                        check[entity.entity] = true
                    }
                }
            }
            let button = {
                id: this.item ? this.item.id : uuid(),
                title: this.title
            }
            if (this.includeThumb) {
                button.image_url = this.thumb_url
            }
            switch (this.type) {
                case ButtonType.GO_TO_STEP:
                    button.payload = this.getGoToStepPayload()
                    break
                case ButtonType.URL:
                    button.url = this.url
                    break
                case ButtonType.CALL_PHONE:
                    button.phone_call = this.phone_call
                    break
                case ButtonType.WEBVIEW:
                    button.webview = this.webview
                    button.webview_params = this.params
                    break
                case ButtonType.KNOWLEDGE:
                    button.request = this.getKnowledgeRequest()
                    break
            }
            this.promise.resolve(button)
            this.close()
        },
        getGoToStepPayload () {
            let obj = this.attributes.reduce((acc, value) => {
                acc[value.variable] = value.value
                return acc
            }, {})
            if (this.isRatingCard) {
                obj.rate_level = this.rate_level
            }
            return this.payload + '#' + this.$ut.b64EncodeUnicode(`{"set_attributes": ${JSON.stringify(obj)}}`)
        },
        getKnowledgeRequest () {
            let dataRequest = { variables: this.variables, entities: this.entities }
            return this.intent + '#' + this.$ut.b64EncodeUnicode(`${JSON.stringify(dataRequest)}`)
        },
        copyPayload () {
            // some how an input outside of dialog can not be selected
            // so i have to created a hidden one inside this dialog
            // fucking stupid vuetify
            let payload = this.getGoToStepPayload() + '#' + this.title
            let el = this.$refs.fuckVuetify
            el.value = payload
            el.select()
            document.execCommand('copy')
        },
        async deleteButton () {
            let confirm = await this.$appService.confirm(this.$t('confirmDelete', { item: this.title }))
            if (confirm) {
                this.promise.resolve(null)
                this.close()
            }
        },
        createStep () {
            this.$appService.useEventBus(
                AppEventBusType.OPEN_STEP_EDITOR,
                null
            ).then(data => {
                this.$store.dispatch(this.$at.scenario.CREATE_STEP, data).then(
                    step => {
                        this.payload = step.name
                    }
                )
            })
        },
        selectThumb () {
            this.$appService.useEventBus(AppEventBusType.SELECT_FILE).then(file => {
                CardService.uploadImage(this.$route.params.botCode, file).then(result => {
                    this.thumb_url = result.data.path
                })
            })
        },
        addScenarioVariable () {
            this.$appService.useEventBus(AppEventBusType.OPEN_VARIABLE_EDITOR, null).then(item => {
                this.$store.dispatch(this.$at.scenario.CREATE_VARIABLE, {
                    name: item.name,
                    entity: item.entity,
                    memory_type_id: item.memory_type_id,
                    is_scenario_scope: false
                }).then(
                    item => {
                        if (this.attributes.length > 0) {
                            this.attributes[this.attributes.length - 1].variable = item.name
                        }
                    }
                )
            })
        },
        cloneExistedButton () {
            this.$appService.useEventBus(AppEventBusType.OPEN_BUTTON_SELECTOR, {
                allowButtonType: this.allowButtonType
            }).then(button => {
                this.updateData(button)
            })
        },
        getVariableItemList (i) {
            return this.variableList.map(variable => ({
                text: variable.name,
                value: variable.name,
                disabled: this.selectedVariableMap[variable.name]
            }))
        },
        getEntityItemList (i) {
            return this.entityList.map(entity => ({
                text: entity.label,
                value: entity.label,
                disabled: this.selectedVariableMap[entity.label]
            }))
        },
        updateVariableMap () {
            for (let i in this.selectedVariableMap) {
                this.selectedVariableMap[i] = false
            }
            for (let attribute of this.attributes) {
                this.selectedVariableMap[attribute.variable] = true
            }
            this.$forceUpdate()
        },
        updateVariableListMap () {
            for (let i in this.selectedVariableListMap) {
                this.selectedVariableListMap[i] = false
            }
            for (let variable of this.variables) {
                this.selectedVariableListMap[variable.variable] = true
            }
            this.$forceUpdate()
        },
        updateEntityListMap () {
            for (let i in this.selectedEntityListMap) {
                this.selectedEntityListMap[i] = false
            }
            for (let entity of this.entities) {
                this.selectedEntityListMap[entity.entity] = true
            }
            this.$forceUpdate()
        }
    }
}
</script>

<style scoped lang="scss">
.ButtonEditor {
    overflow: auto;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    position: relative;

    .v-form {
        flex: 1 1 400px;
    }

    .hidden-input {
        position: absolute;
        left: 0;
        top: -100px;
    }
}

table {
    width: 100%;
    table-layout: fixed;

    tr td {
        padding: 5px 0;

        &:first-child {
            width: calc(50% - 18px);
        }

        &:last-child {
            width: 36px;
        }
    }
}

.thumb-icon {
    border: 1px solid #D0D4E2;
    padding: 5px;
    border-radius: 5px;
    cursor: pointer;

    img {
        height: 30px;
        display: block;
    }
}
</style>
